import React from 'react';
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import PropTypes from 'prop-types';

import { GetJson, PostJson, DeleteJson, PutJson } from '@awp/helpers/fetchReact';
import { REST_API, STATE_STATUS } from '@awp/constants';
import { create } from 'ol/transform';

const initialState = {
  variableMessageDetails: [],
  status: {
    detailedInfo: STATE_STATUS.IDLE,
    stateList: STATE_STATUS.IDLE,
    stateSet: STATE_STATUS.IDLE,
    stateTimer: STATE_STATUS.IDLE,
    error: null,
  },
};

export const selectStatus = (state) => state.variableMessageSign.status;
export const selectVMSDetails = (state) => state.variableMessageSign.variableMessageDetails;
export const selectStatusError = (state) => state.variableMessageSign.status.error;

/**
 * Thunks
 */
export const fetchDetailedVMSInfo = createAsyncThunk('variableMessageSign/fetchDetailedVMSInfo', async () => {
  let url = REST_API.VMS_DETAILED_INFO;
  const response = await GetJson(url);
  let data = await response.json();
  if (data && data.length > 0) return data.sort((a, b) => a.Location.localeCompare(b.Location));
  return data;
});

export const fetchVMSStateList = createAsyncThunk('variableMessageSign/fetchVMSStateList', async () => {
  let url = REST_API.VMS_STATE_LIST;
  const response = await GetJson(url);
  return await response.json();
});

export const setVMSTimer = createAsyncThunk('variableMessageSign/setVMSTimer', async (payload, { getState }) => {
  const { vmsSignId, resetTime } = payload;

  let url = `${REST_API.VMS_TIMER}/${vmsSignId}`;
  let dto = {
    ResetTime: resetTime,
  };

  const response = await PostJson(url, dto);
  return await response.json();
});

export const cancelVMSTimer = createAsyncThunk('variableMessageSign/cancelVMSTimer', async (payload, { getState }) => {
  const { vmsSignId } = payload;

  let url = `${REST_API.VMS_TIMER}/${vmsSignId}`;

  const response = await DeleteJson(url);
  return await response.json();
});

export const setVMSState = createAsyncThunk('variableMessageSign/setVMSState', async (payload, { getState }) => {
  const { vmsSignId, weatherStationId, weatherStationNumber, stateKey, stateValue } = payload;

  let url = REST_API.VMS_STATE + weatherStationId;
  let dto = {
    VariableMessageSignId: vmsSignId,
    type: 'SET_VMS_STATE',
    payload: { stationId: weatherStationNumber, key: stateKey, value: stateValue },
  };

  const response = await PutJson(url, dto);
  return await response.json();
});

export const variableMessageSignSlice = createSlice({
  name: 'variableMessageSign',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDetailedVMSInfo.pending, (state) => {
        state.status.detailedInfo = STATE_STATUS.LOADING;
      })
      .addCase(fetchDetailedVMSInfo.fulfilled, (state, action) => {
        state.variableMessageDetails = adjustVMSData(action.payload); //Object.values(vmsData);
        state.status.detailedInfo = STATE_STATUS.SUCCESS;
      })
      .addCase(fetchDetailedVMSInfo.rejected, (state, action) => {
        state.error = action.error.message;
        state.status.detailedInfo = STATE_STATUS.FAILED;
      })
      .addCase(setVMSState.pending, (state) => {
        state.status.stateSet = STATE_STATUS.LOADING;
        state.status.stateTimer = STATE_STATUS.IDLE;
      })
      .addCase(setVMSState.fulfilled, (state, action) => {
        if (action.payload.entity) {
          state.status.stateSet = STATE_STATUS.FAILED;
          state.status.error = action.payload.entity;
        } else {
          state.variableMessageDetails = adjustVMSData(action.payload);
          state.status.stateSet = STATE_STATUS.SUCCESS;
        }
      })
      .addCase(setVMSState.rejected, (state, action) => {
        state.error = action.payload.entity;
        state.status.stateSet = STATE_STATUS.FAILED;
      })
      .addCase(setVMSTimer.pending, (state) => {
        state.status.stateTimer = STATE_STATUS.LOADING;
        state.status.stateSet = STATE_STATUS.IDLE;
      })
      .addCase(setVMSTimer.rejected, (state, action) => {
        state.error = action.error.message;
        state.status.stateTimer = STATE_STATUS.FAILED;
      })
      .addCase(setVMSTimer.fulfilled, (state, action) => {
        if (action.payload.entity) {
          state.status.stateTimer = STATE_STATUS.FAILED;
          state.status.error = action.payload.entity;
        } else {
          state.variableMessageDetails = adjustVMSData(action.payload);
          state.status.stateTimer = STATE_STATUS.SUCCESS;
        }
      })
      .addCase(cancelVMSTimer.pending, (state) => {
        state.status.stateTimer = STATE_STATUS.LOADING;
      })
      .addCase(cancelVMSTimer.rejected, (state, action) => {
        state.error = action.error.message;
        state.status.stateTimer = STATE_STATUS.FAILED;
      })
      .addCase(cancelVMSTimer.fulfilled, (state, action) => {
        if (action.payload.entity) {
          state.status.stateTimer = STATE_STATUS.FAILED;
          state.status.error = action.payload.entity;
        } else {
          state.variableMessageDetails = adjustVMSData(action.payload);
          state.status.stateTimer = STATE_STATUS.SUCCESS;
        }
      });
  },
});

const adjustVMSData = (rawVMSData) => {
  // going to deal with the sign values here, splitting into lines and pages
  let vmsData = rawVMSData.map((item) => {
    let signPages = item.ObservedCharacterValue.split('|');
    if (signPages.length == 1) {
      signPages = [signPages[0], signPages[0]];
    }
    let signInfo = signPages.map((value) => {
      let changedSignInfo = value.replace(/~/g, '<br>');
      if (!changedSignInfo.includes('<br>')) {
        changedSignInfo += '<br>&nbsp;';
      }
      if (changedSignInfo.endsWith('<br>')) {
        changedSignInfo += '&nbsp;';
      }

      return changedSignInfo;
    });

    return {
      ...item,
      signValue: signInfo,
    };
  });

  //group the vmsData by WeatherStation
  /*vmsData = vmsData.reduce((acc, item) => {
    const { WeatherStationId, WeatherStationName } = item;
    if (!acc[WeatherStationId]) {
      acc[WeatherStationId] = {
        WeatherStationId,
        WeatherStationName,
        vmsSigns: [],
      };
    }
    acc[WeatherStationId].vmsSigns.push(item);
    return acc;
  }, {});*/

  // group the vmsData by SignName
  vmsData = vmsData.reduce((acc, item) => {
    const { Location } = item;
    if (!acc[Location]) {
      acc[Location] = {
        Location,
        //WeatherStationId: item.WeatherStationId, //same location may have multi-stations, removed this
        vmsSigns: [],
      };
    }
    acc[Location].vmsSigns.push(item);
    return acc;
  }, {});

  return Object.values(vmsData);
};
